/**
 * OpenSopButton: the course player's "Open the SOP" (plan
 * docs/plans/lms-plan.md 11.3 "a 'Open the SOP' button that opens the
 * presigned file in a new tab", D15, 18.25, 18.27; WP-46). The reading and
 * acknowledge stages of an SOP course (category sop) show it before "Mark as
 * read" and before signing; the generated SOP training tells the learner to
 * use it.
 *
 * On the click: the tab opens at once (OpenFileButton, the DoneStage
 * technique), then GET /sops/by-course/:courseId?course_version_id=<the
 * version shown> finds the SOP version to open, then GET /api/lms<url_path>
 * mints its presigned URL ({ url, filename }, 10 minutes), and the tab is
 * pointed at it.
 *
 * Props:
 *   courseId          string          the SOP course
 *   courseVersionId?  string | null   the training version the player shows (18.25: the SOP version linked to it)
 *   variant?          'outline' | 'primary'
 *   testId?           string          default "lms-open-sop"
 *   onOpened?         () => void      the file's URL was minted (sop-import-plan 4.4 item 3: the reading stage's gate)
 *   onUnavailable?    () => void      the server has no file this person may open behind the training (no file,
 *                                     or 404): the reading stage then stops holding its control, so nobody is
 *                                     stuck behind a file they cannot be given
 */
import { getSopFileUrlByPath, getSopForCourse } from '../../api/sops';
import { OpenFileButton, OpenFileError } from './OpenFileButton';

export interface OpenSopButtonProps {
  courseId: string;
  courseVersionId?: string | null;
  variant?: 'outline' | 'primary';
  testId?: string;
  onOpened?: () => void;
  onUnavailable?: () => void;
}

/** A refusal that says no file can be opened here (no file behind the training, or 404), not a passing failure. */
export function isFileUnavailable(err: unknown): boolean {
  if (err instanceof OpenFileError) return true;
  return (err as { response?: { status?: number } } | null)?.response?.status === 404;
}

/** The presigned URL of the SOP file behind a training version (18.25). */
export async function resolveSopFileForCourse(courseId: string, courseVersionId?: string | null): Promise<{ url: string; filename: string }> {
  const found = await getSopForCourse(courseId, courseVersionId ?? null);
  if (!found?.url_path) {
    throw new OpenFileError('This SOP has no file you can open yet. Ask a Learning Center admin.');
  }
  return getSopFileUrlByPath(found.url_path);
}

export function OpenSopButton({ courseId, courseVersionId = null, variant = 'outline', testId = 'lms-open-sop', onOpened, onUnavailable }: OpenSopButtonProps) {
  return (
    <OpenFileButton
      resolve={() => resolveSopFileForCourse(courseId, courseVersionId)}
      label="Open the SOP"
      variant={variant}
      testId={testId}
      failureMessage="The SOP could not be opened. Try again."
      onOpened={onOpened}
      onFailed={(err) => {
        if (onUnavailable && isFileUnavailable(err)) onUnavailable();
      }}
    />
  );
}

export default OpenSopButton;
